/**
 * Standing transport intent — the operator's sticky "play / paused" wish.
 *
 * This is deliberately NOT the same thing as "audio is sounding right now":
 * a track boundary, a timed ad fire, a daypart change, a resync or a failure
 * retry all load a new entry, and each of those used to start audio on its
 * own. An operator who pressed pause then heard the venue come back to life
 * seconds later, with no command sent — the schedule simply overrode them
 * (observed live, desktop run r_msuimk6vpv2w1x9f: a pause at 15:19:50 was
 * undone by three ad fires and a music boundary).
 *
 * So every entry start funnels through `shouldAutoPlayEntry`, and the intent
 * flag it reads changes ONLY on an operator transport action (play / pause /
 * stop / an explicit play-now). A paused player still tracks the schedule —
 * it loads what it would have played, so resuming plays the right thing — but
 * it emits no audio until the operator says so.
 */

/**
 * The intent a freshly-started player boots with. A claimed player is a venue
 * appliance: after a power cycle, app restart or OTA reload it must resume its
 * scheduled program on its own, with no operator or console to press play.
 * Seeding `false` instead would make the sticky-pause rule silence every
 * kiosk at boot.
 */
export const INITIAL_WANT_PLAYING = true;

export interface AutoPlayIntent {
  /** The caller asked for playback (the `autoPlay` argument of the start). */
  requested: boolean;
  /** The standing operator intent (`wantPlayingRef`). */
  wantPlaying: boolean;
  /** A live broadcast announcement holds the audio bus — nothing else sounds. */
  broadcastActive: boolean;
  /**
   * The operator explicitly chose THIS entry right now (play-now for an item
   * or playlist, starting a channel). Such a start carries its own intent and
   * therefore lifts a pause; a scheduler-driven interrupt does not. Keying
   * this on `entry.interrupt` instead is the hole the ad fires came through:
   * every scheduled announcement/ad is an interrupt too.
   */
  manual: boolean;
}

/** Whether a freshly loaded entry actually starts sounding. */
export function shouldAutoPlayEntry(intent: AutoPlayIntent): boolean {
  if (!intent.requested) return false;
  if (intent.broadcastActive) return false;
  return intent.wantPlaying || intent.manual;
}

export interface TimedFireAudioPlan {
  /** Let this fire's audio entries reach the audio program at all. */
  takeAudio: boolean;
  /** Pull the next entry onto the bus. */
  advance: boolean;
  /** …and let it sound. */
  autoPlay: boolean;
}

/**
 * What a timed insertion fire (an ad, an announcement) is allowed to do to the
 * AUDIO bus. The screen bus is a separate transport and is never gated here.
 *
 * While the operator has paused, the fire is dropped rather than loaded
 * silently. Three reasons, all observed: nobody can hear it, so it is spent on
 * an empty room either way; a silently-loaded insertion never ends (no
 * `didJustFinish` without playback) so it would squat on the bus for the whole
 * pause and the console would name a paused AD instead of the track the
 * operator paused on; and on resume it would play minutes or hours stale. The
 * bed keeps tracking the schedule through the ordinary boundary/sync path, so
 * resuming still plays the right thing.
 */
export function planTimedFireAudio(input: {
  /** The fire resolved at least one audio item. */
  hasAudio: boolean;
  /** This fire's intent takes over the audio bus (av_full / audio_only). */
  interrupting: boolean;
  /** Nothing is on the audio bus right now. */
  busIdle: boolean;
  /** The standing operator intent. */
  wantPlaying: boolean;
}): TimedFireAudioPlan {
  if (!input.wantPlaying) {
    return { takeAudio: false, advance: input.busIdle, autoPlay: false };
  }
  const interrupting = input.hasAudio && input.interrupting;
  if (!interrupting && !input.busIdle) {
    return { takeAudio: true, advance: false, autoPlay: false };
  }
  return { takeAudio: true, advance: true, autoPlay: true };
}

export type TransportState = "playing" | "paused" | "stopped";

/**
 * The transport state a now-playing report carries. "paused" is a state WITH
 * an item: a paused player must keep naming what it is paused on, or every
 * control surface reading the report (the mobile Controls card, the web
 * console) collapses it to an idle "Nothing playing" and the operator cannot
 * tell a paused venue from a dead one.
 */
export function reportedTransportState(input: {
  /** Something is loaded on the bus (playing or paused on it). */
  hasEntry: boolean;
  /** That entry is actually producing sound right now. */
  sounding: boolean;
}): TransportState {
  if (!input.hasEntry) return "stopped";
  return input.sounding ? "playing" : "paused";
}

/**
 * True when the report names an entry the player has LOADED but never sounded
 * — the silent load a boundary, resync or drift rejoin performs under a
 * standing pause.
 *
 * The three transport states cannot express that on their own, and both of the
 * other readings are wrong:
 *
 * - Reporting it as an ordinary "paused" (what shipped first) makes the entry
 *   claim its own item id as a started play. The gateway's play-log gate then
 *   sees the operator's resume as a re-report of something already on air and
 *   writes NO play_log row for it. A deferred AD loaded this way plays to the
 *   venue with no proof of play and under-credits the advertiser.
 * - Reporting it as "stopped" fixes the log and brings back the bug the sticky
 *   pause fixed: every control surface collapses a stopped device to "Nothing
 *   playing", so a paused venue would go nameless the moment a boundary passed.
 *
 * So the report keeps its state AND its item, and carries this flag: the
 * console still names what the venue is cued on, and the gateway counts the
 * entry as not started until it actually sounds.
 *
 * `everSounded` is per LOADED ENTRY — it resets whenever a new entry takes the
 * bus and turns true the moment that entry is reported as playing.
 */
export function isCuedReport(input: {
  state: TransportState;
  /** Something is loaded on the bus. */
  hasEntry: boolean;
  /** This entry has produced sound at least once since it was loaded. */
  everSounded: boolean;
}): boolean {
  return input.state === "paused" && input.hasEntry && !input.everSounded;
}

/**
 * Whether applying a fresh sync payload may pull a NEW entry onto the audio
 * bus.
 *
 * Same class as the timed-fire rule above: while the operator has paused, a
 * plan-driven announcement takeover must not swap the entry they paused ON.
 * Doing so loads the announcement silently over that track, so the console
 * names something the operator never chose for the whole pause and resuming
 * plays it.
 *
 * Three starts survive a pause, because none of them replaces a choice the
 * operator can still make:
 *
 * - an EMPTY bus (the silent load is how a paused player keeps tracking the
 *   schedule, so resuming plays the right thing);
 * - a player parked on an item that FAILED (a repair, not a schedule swap);
 * - a CONTENT CUT, where the resync says the on-air entry did not survive at
 *   all: the slot went inactive, the playlist was swapped, or the item was
 *   removed. Parking on it would keep a DEAD entry cued and play it once on
 *   resume — and if that entry is an ad whose flight has ended, it airs and
 *   earns a play_log row, the mirror image of the pause bug. The cut moves the
 *   bus to whatever the new content says; the pause is still honoured, because
 *   the start itself runs through `shouldAutoPlayEntry` (silent, reported cued)
 *   like every other scheduler-driven start.
 */
export function shouldRestartOnSync(input: {
  /** A new announcement takeover from the plan wants the bus now. */
  interrupted: boolean;
  /** Nothing is on the audio bus. */
  busIdle: boolean;
  /** The standing operator intent. */
  wantPlaying: boolean;
  /** The on-air entry did not survive the content change (soft-resync cut). */
  contentCut?: boolean;
  /** The bus is parked on an item that failed and needs a repair start. */
  awaitingFailureRecovery?: boolean;
}): boolean {
  if (input.busIdle) return true;
  if (input.awaitingFailureRecovery) return true;
  if (input.contentCut) return true;
  if (!input.wantPlaying) return false;
  return input.interrupted;
}
